import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { RouterModule } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ChaveAcessoService } from '../../../shared/services/apis/integracao/chave-acesso.service';
import { ListaChaveAcessoResponse } from '../../../shared/services/responses/integracao/lista-chave-acesso.response';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { ChaveAcesso } from '../../../shared/services/models/integracao/chave-acesso.model';
import { UsuarioService } from '../../../shared/services/apis/usuario/usuario.service';
import { ListaUsuarioResponse } from '../../../shared/services/responses/usuario/lista-usuario.response';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { MatMenuModule } from '@angular/material/menu';
import { UtilService } from '../../../shared/services/util.service';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-chave-acesso-lista',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ClipboardModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    EmptyListComponent,
    MatMenuModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './chave-acesso-lista.component.html',
  styleUrls: ['./chave-acesso-lista.component.css'],
})
export class ChaveAcessoListaComponent implements OnInit {
  public moduloEnum = ModuloEnum.Integracao; // Ajuste conforme seu Enum
  public funcionalidadeEnum = FuncionalidadeEnum.Integracao_ChaveAcesso; // Ajuste conforme seu Enum
  public model: FormGroup;
  public _isLoading = false;
  public _ultimaPagina = false;
  public _paginaAtual = 0;
  public listaChaveAcessoResponse: ListaChaveAcessoResponse | undefined;
  public _dataSource = new MatTableDataSource<ChaveAcesso>();
  public displayedColumns: string[] = [
    'idChaveAcesso',
    'chaveAcessoNome',
    'nomeUsuario',
    'ativo',
    'chave',
    'actions',
  ];

  constructor(
    private formBuilder: FormBuilder,
    private chaveAcessoService: ChaveAcessoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private clipboard: Clipboard,
    private usuarioService: UsuarioService,
    private utilService: UtilService
  ) {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/integracao/chave-acesso/cadastrar',
    ]);
  }

  ngOnInit(): void {
    this.loadFilters();
    this.pesquisar();
  }

  private loadFilters() {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  clickPesquisar(): void {
    this._paginaAtual = 0;
    this.listaChaveAcessoResponse = undefined;
    this._dataSource.data = [];
    this.pesquisar();
  }

  pesquisar(): void {
    if (this._isLoading) {
      return;
    }

    this._isLoading = true;
    const pesquisa = this.model.get('pesquisa')?.value || '';

    this.chaveAcessoService.getChavesAcesso(pesquisa).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        if (!this.listaChaveAcessoResponse) {
          this.listaChaveAcessoResponse = response;
        } else {
          this.listaChaveAcessoResponse.listaChaveAcesso.push(...response.listaChaveAcesso);
        }
        this._dataSource.data = this.listaChaveAcessoResponse!.listaChaveAcesso;
        this._ultimaPagina = response.listaChaveAcesso.length < 10; // Assumindo paginação de 10
        this.carregarUsuarios(response.listaChaveAcesso);
      },
      error: error => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }

  carregarUsuarios(listaChaveAcesso: ChaveAcesso[]): void {
    this.usuarioService.getAll().subscribe({
      next: (response: ListaUsuarioResponse) => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }
        this._dataSource.data.forEach(x => {
          response.listaUsuario.forEach(c => {
            if (x.idUsuario == c.idUsuario) {
              x.nomeUsuario = c.nomeUsuario;
            }
          });
        });
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show('Erro inesperado', 'Erro ao buscar nome de usuários', AlertType.error);
      },
    });
  }

  proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  copiarChave(chave: string): void {
    this.clipboard.copy(chave);
    this.alertService.show('Sucesso', 'Chave copiada para a área de transferência!', AlertType.success);
  }

  abrirModalExcluir(id: number, nome: string): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmar Exclusão',
        description: `Você realmente deseja excluir a chave "${nome}"? Qualquer integração que utiliza ela irá parar de funcionar instantaneamente.`,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.excluirChaveAcesso(id);
      }
    });
  }

  excluirChaveAcesso(idChaveAcesso: number): void {
    this.chaveAcessoService.deletarChave(idChaveAcesso).subscribe({
      next: (response: ReturnStruct) => {
        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
        } else {
          this.alertService.show('Sucesso', 'Chave de acesso excluída com sucesso.', AlertType.success);
          this.clickPesquisar();
        }
      },
      error: error => {
        this.alertService.show('Erro inesperado', error.message, AlertType.error);
      },
    });
  }
}
